/**
 * GoogleReviewsEmbed — live Google Reviews via the Featurable JSON API.
 *
 * Why this approach:
 *   • Featurable already maintains the bridge to Google so we don't have
 *     to wrangle the Places API ourselves, deal with referer restrictions,
 *     or pay for API requests.
 *   • Their JSON endpoint lets us render the reviews with our own card
 *     design (matches the rest of the light Summer Camps theme) instead
 *     of dropping in their script and inheriting their layout.
 *   • No external <script> tag → page weight stays low, no CSP changes.
 *
 * Endpoint shape (fetched once per mount):
 *   { totalReviewCount, averageRating, profileUrl,
 *     reviews: [{ reviewer: { displayName, profilePhotoUrl },
 *                 starRating, comment, createTime, reviewReply? }] }
 *
 * UX details:
 *   • Skeleton cards while the request is in flight so the layout
 *     doesn't pop.
 *   • A "Write a review" outbound button links straight to the Google
 *     review-writing modal (uses `profileUrl` from the response).
 *   • Comments are clamped to 6 lines so cards stay even; the carousel
 *     on mobile still works (CarouselGrid handles the scroll behavior).
 */
import { Star, ExternalLink } from 'lucide-react';
import SectionHeading from './SectionHeading';
import CarouselGrid from './CarouselGrid';
import useGoogleReviews from '../../hooks/useGoogleReviews';

const FALLBACK_WRITE_URL = `https://search.google.com/local/writereview?placeid=ChIJhVpN1Y_7DogRNWwAwgGikvA`;

function GoogleG({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

function Stars({ value = 5 }) {
  const filled = Math.round(value);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${value} of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < filled ? 'fill-amber-400 text-amber-400' : 'fill-rsc-card-border text-rsc-card-border'
          }`}
        />
      ))}
    </div>
  );
}

// Format Google's RFC3339 timestamp into "Aug 2025" style — relative
// dates feel less authoritative for a review block.
function fmtMonth(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

function ReviewCard({ review }) {
  const name = review?.reviewer?.displayName || 'Google user';
  const photo = review?.reviewer?.profilePhotoUrl;
  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <article
      className="h-full rounded-2xl bg-white border border-rsc-card-border shadow-sm p-5 flex flex-col"
      data-testid={`review-card-${review?.reviewId || 'r'}`}
    >
      <div className="flex items-center gap-3">
        {photo ? (
          <img
            src={photo}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            className="w-10 h-10 rounded-full object-cover border border-rsc-card-border bg-rsc-slate-soft"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rsc-slate-soft to-white border border-rsc-card-border flex items-center justify-center text-rsc-brand-blue font-extrabold">
            {initial}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-extrabold text-rsc-brand-blue truncate">{name}</div>
          <div className="text-[10px] uppercase tracking-wider text-rsc-charcoal-muted">
            {fmtMonth(review?.createTime)} · Google review
          </div>
        </div>
        <GoogleG className="w-4 h-4 flex-shrink-0" />
      </div>
      <Stars value={review?.starRating ?? 5} />
      <p
        className="mt-2 text-sm text-rsc-charcoal leading-relaxed italic"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 6,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        &ldquo;{review?.comment || ''}&rdquo;
      </p>
    </article>
  );
}

function ReviewSkeleton() {
  return (
    <article className="h-full rounded-2xl bg-white border border-rsc-card-border shadow-sm p-5 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-rsc-slate-soft" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-24 bg-rsc-slate-soft rounded" />
          <div className="h-2 w-16 bg-rsc-slate-soft rounded" />
        </div>
      </div>
      <div className="mt-3 h-3 w-20 bg-rsc-slate-soft rounded" />
      <div className="mt-3 space-y-2">
        <div className="h-2 w-full bg-rsc-slate-soft rounded" />
        <div className="h-2 w-11/12 bg-rsc-slate-soft rounded" />
        <div className="h-2 w-3/4 bg-rsc-slate-soft rounded" />
      </div>
    </article>
  );
}

export default function GoogleReviewsEmbed() {
  const { data, error } = useGoogleReviews();

  // Filter to 4★+ comments with non-empty text so the carousel doesn't
  // surface low-signal "thumbs up only" reviews.
  const reviews = (data?.reviews || []).filter(
    (r) => (r.starRating ?? 0) >= 4 && (r.comment || '').trim().length > 0,
  );
  const avg = data?.averageRating ?? 5;
  const total = data?.totalReviewCount ?? 0;
  const writeUrl = data?.profileUrl || FALLBACK_WRITE_URL;

  return (
    <section id="testimonials" className="bg-rsc-slate py-16 sm:py-20 font-poppins scroll-mt-32" data-testid="google-reviews">
      <div className="container-app">
        <SectionHeading
          eyebrow="From families"
          title="Parents"
          accent="keep coming back."
          body="Real reviews from families across our communities, pulled live from Google."
        />

        {/* Summary card */}
        <div className="mt-8 max-w-3xl mx-auto rounded-2xl bg-white border border-rsc-card-border shadow-sm p-5 flex flex-wrap items-center gap-4 sm:gap-5">
          <GoogleG className="w-10 h-10" />
          <div className="text-4xl font-extrabold text-rsc-brand-blue leading-none">
            {avg ? avg.toFixed(1) : '5.0'}
          </div>
          <div className="flex flex-col">
            <Stars value={avg || 5} />
            <span className="text-xs text-rsc-charcoal-muted mt-1">
              Based on {total || '—'} Google reviews
            </span>
          </div>
          <a
            href={writeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rsc-yellow text-rsc-brand-blue text-xs font-extrabold hover:bg-rsc-yellow/90 shadow-sm shadow-amber-200/50 transition-all"
            data-testid="write-review-btn"
          >
            Write a review
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Review cards — live data once loaded */}
        <div className="mt-6">
          {!data && !error && (
            <div className="grid md:grid-cols-3 gap-4">
              <ReviewSkeleton />
              <ReviewSkeleton />
              <ReviewSkeleton />
            </div>
          )}

          {error && (
            <p className="text-center text-sm text-rsc-charcoal-muted">
              Reviews are temporarily unavailable —
              {' '}
              <a
                href={writeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-rsc-blue underline"
              >
                see them on Google
              </a>.
            </p>
          )}

          {!error && reviews.length > 0 && (
            <CarouselGrid variant="reviews" data-testid="reviews-grid">
              {reviews.slice(0, 9).map((r, i) => (
                <ReviewCard key={r.reviewId || i} review={r} />
              ))}
            </CarouselGrid>
          )}
        </div>
      </div>
    </section>
  );
}
